/*
 | ==========================================================
 | This File is for member hub rewards state getters handling
 | ==========================================================
 */
import memberhubConstants from '~/constants/memberhub.constants'

/*
 * Display reference id for a reward / gift card. Kept private to the store so
 * the slider and history getters share one source of truth (fallback chain:
 * live submit id → opportunity id → reward id, suffixed with the card type).
 */
const buildReferenceId = reward => {
  const submitId = reward.extras?.LIVE_SUBMIT_ID || reward.extras?.live_submit_id || reward.opportunity_id || reward.id
  return `${submitId}-${reward.gc_card_type}`
}

// Stable id for the single-slide placeholder states (only one is ever rendered
// at a time, so reusing the same id across the two placeholder lists is safe).
const PLACEHOLDER_ID = 'placeholder'

const getters = {
  /*
   * Accessor for rewards List in slider
   * @param state
   * @returns {*}
   */
  rewardsSlider: state => {
    return state.rewards.filter(reward => [memberhubConstants.GIFT_CARD_STATUS.PENDING, memberhubConstants.GIFT_CARD_STATUS.ACTIVATED, memberhubConstants.GIFT_CARD_STATUS.AVAILABLE].includes(reward.status))
  },
  /*
   * Accessor for rewards List in history
   * @param state
   * @returns {*}
   */
  rewardsHistory: state => {
    return state.rewards.filter(reward => [memberhubConstants.GIFT_CARD_STATUS.ARCHIVED, memberhubConstants.GIFT_CARD_STATUS.INELIGIBLE, memberhubConstants.GIFT_CARD_STATUS.EXPIRED].includes(reward.status))
  },
  /*
   * View-shaped slider cards consumed by the wallet swiper (FlipCard's
   * `giftCardConfig`). The reward → card mapping lives here so the page and
   * history share the store as the single source of gift-card display data.
   * @returns {{id: *, status: number, data: object}[]}
   */
  sliderCards () {
    return this.rewardsSlider.map(reward => {
      const status = parseInt(reward.status)
      return {
        id: reward.id,
        status,
        data: {
          value: reward.gc_amount,
          expires_on: reward.date_expire,
          available_on: reward.date_available,
          reference_id: buildReferenceId(reward),
          source: reward.source,
          archive: status === memberhubConstants.GIFT_CARD_STATUS.ACTIVATED,
          gc_link: reward.claim_url_link
        }
      }
    })
  },
  /*
   * History rewards augmented with their display `reference_id`, consumed by
   * the history table/grid.
   * @returns {object[]}
   */
  historyCards () {
    return this.rewardsHistory.map(reward => ({ ...reward, reference_id: buildReferenceId(reward) }))
  },
  /*
   * The final wallet listing rendered by the swiper: real slider cards for a
   * verified user, a single blurred placeholder while their email is unverified,
   * or the no-gift-cards placeholder when there are none. Owned by the store so
   * the page is a thin, reactive consumer.
   * @returns {{id: *, status: number|string, data?: object}[]}
   */
  walletCards () {
    const cards = this.sliderCards
    if (!cards.length) {
      return [{ id: PLACEHOLDER_ID, status: 'nil' }]
    }
    return useUserStore().isEmailVerified
      ? cards
      : [{ id: PLACEHOLDER_ID, status: 'unverified' }]
  },
  /*
   * Whether a cached rewards list exists (SWR warm render)
   */
  hasCachedRewards: state => state.rewards.length > 0,
  /*
   * Bumped only when a refresh actually changed the list
   */
  rewardsRevision: state => state.rewards_revision,
  /*
   * hash_id owning the persisted cache (user-switch guard)
   */
  getRewardsUserId: state => state.rewards_user_id
}
export default getters
